import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useAtomValue } from 'jotai';
import { useNavigate } from '@tanstack/react-router';

import { KpiSummary } from './kpi-summary';
import { PerformanceOverTime } from './performance-over-time';
import { OverviewTab } from '../tabs/overview-tab';
import { usePerformanceTimeSeriesQuery, metaTimeSeriesQueryOptions, metaIntegrationStatusQueryOptions } from '@/lib/query-options';
import { useCurrencySymbol } from '@/hooks/useCurrencySymbol';
import { usePerformanceData, useScopedCampaigns } from '@/hooks/usePerformanceData';
import { toDisplayAmount } from '@/lib/performance/money';
import { dateWindow } from '@/lib/performance/date-window';
import { platformsInScope, type PlatformScope } from '@/lib/performance/types';
import type { MetricsByPlatform, PlatformMetrics } from '@/lib/performance/kpis';
import type { WidgetConfig } from '@/components/glitch-ui/widget-grid';
import {
  consolidatedPerformanceDateRangeAtom,
  consolidatedPerformanceGroupByAtom,
  consolidatedPerformanceComparisonAtom,
  consolidatedPerformanceCampaignTypesAtom,
  consolidatedPerformanceCampaignIdsAtom,
  consolidatedPerformanceIncludeExternalAtom,
} from '@/store';

const ZERO: PlatformMetrics = { spend: 0, results: 0, clicks: 0, impressions: 0 };

/**
 * Shared Overview data (#5971, CP-6). The KPIs widget and the chart widget both
 * consume it; React Query dedupes the underlying fetches by query key, so they
 * render as independent draggable widgets without double-fetching.
 */
function useOverviewData(scope: PlatformScope) {
  const inScope = platformsInScope(scope);
  const googleInScope = inScope.includes('google');
  const metaInScope = inScope.includes('meta');

  // Honor the toolbar's date range through the shared window helper (#6274) so
  // the top block, the Google detail widgets and the Meta series all cover the
  // exact same calendar days.
  const selectedDays = useAtomValue(consolidatedPerformanceDateRangeAtom);
  const window = useMemo(() => dateWindow(selectedDays), [selectedDays]);

  // #6277 — the same campaign scoping as every other Google widget (the old
  // 'all' sentinel hit the account-wide backend branch, which is silently
  // Search-only for its original Goals-baseline consumer, and ignored the
  // campaign filters). Glitch ids: the time-series route maps them server-side.
  const campaignTypes = useAtomValue(consolidatedPerformanceCampaignTypesAtom);
  const selectedCampaignIds = useAtomValue(consolidatedPerformanceCampaignIdsAtom);
  const includeExternal = useAtomValue(consolidatedPerformanceIncludeExternalAtom);
  const typeFilter = useMemo<(type: string) => boolean>(() => {
    if (campaignTypes.size === 0 || campaignTypes.has('all')) return () => true;
    return (type: string) => campaignTypes.has(type);
  }, [campaignTypes]);
  const { scopedCampaigns } = useScopedCampaigns({ includeExternal, typeFilter, selectedCampaignIds });
  const googleChartIds = useMemo(() => scopedCampaigns.map((c) => c.id).join(','), [scopedCampaigns]);

  const { symbol } = useCurrencySymbol();
  const currencySymbol = symbol ?? '';

  const { data: googleRows, isLoading: googleLoading } = usePerformanceTimeSeriesQuery(
    'daily',
    `${window.days}d`,
    googleInScope && googleChartIds ? googleChartIds : undefined,
    { startDate: window.start_date, endDate: window.end_date },
  );
  // #6278 — an unconnected Meta integration must not render "Meta €0" KPI lines
  // (an empty series is indistinguishable from a connected zero-spend account).
  const { data: metaStatus } = useQuery({ ...metaIntegrationStatusQueryOptions(), enabled: metaInScope });
  const metaConnected = metaInScope && !!metaStatus?.is_connected;
  const { data: metaResp, isLoading: metaLoading } = useQuery(
    metaTimeSeriesQueryOptions(window.start_date, window.end_date, metaConnected),
  );

  const metrics = useMemo<MetricsByPlatform>(() => {
    const out: MetricsByPlatform = {};
    if (googleInScope && googleRows) {
      out.google = googleRows.reduce<PlatformMetrics>(
        (acc, r) => ({
          spend: acc.spend + (r.cost ?? 0),
          results: acc.results + (r.conversions ?? 0),
          clicks: acc.clicks + (r.clicks ?? 0),
          impressions: acc.impressions + (r.impressions ?? 0),
        }),
        { ...ZERO },
      );
    }
    if (metaConnected && metaResp) {
      out.meta = metaResp.rows.reduce<PlatformMetrics>(
        (acc, r) => ({
          spend: acc.spend + toDisplayAmount(r.spend_cents, 'meta'),
          results: acc.results + (r.leads ?? 0),
          clicks: acc.clicks + (r.clicks ?? 0),
          impressions: acc.impressions + (r.impressions ?? 0),
        }),
        { ...ZERO },
      );
    }
    return out;
  }, [googleInScope, googleRows, metaConnected, metaResp]);

  const loading = (googleInScope && googleLoading) || (metaConnected && metaLoading);
  return { metrics, googleRows, metaRows: metaResp?.rows, currencySymbol, loading };
}

/** Overview KPI summary tiles — a draggable/resizable widget. */
export function OverviewKpis({ scope }: { scope: PlatformScope }) {
  const { metrics, currencySymbol, loading } = useOverviewData(scope);
  if (loading) {
    // Skeleton tiles instead of a bare "Loading…" (#6281) — a cold multi-chunk
    // GAQL fetch can take many seconds, during which the empty block read as a
    // broken page.
    return (
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        {['spend', 'impressions', 'clicks', 'conversions'].map((k) => (
          <div key={k} className="rounded-lg border bg-white p-4 shadow-sm">
            <div className="h-3 w-20 animate-pulse rounded bg-gray-100" />
            <div className="mt-2 h-7 w-24 animate-pulse rounded bg-gray-100" />
          </div>
        ))}
      </div>
    );
  }
  return <KpiSummary metrics={metrics} scope={scope} currencySymbol={currencySymbol} />;
}

/** Overview performance-over-time chart — a draggable/resizable widget. */
export function OverviewChart({ scope }: { scope: PlatformScope }) {
  const { googleRows, metaRows, currencySymbol } = useOverviewData(scope);
  // #6276 — the toolbar's Group By now aggregates this chart (it was inert).
  const groupBy = useAtomValue(consolidatedPerformanceGroupByAtom);
  return (
    <PerformanceOverTime
      scope={scope}
      googleRows={googleRows}
      metaRows={metaRows}
      currencySymbol={currencySymbol}
      groupBy={groupBy}
    />
  );
}

// The Google detail grid reuses the full perf-v2 Overview widget layout minus
// 'performance-chart' — the cross-platform PerformanceOverTime in the top block
// already covers time-series, so we drop the Google-only duplicate. This key is
// the tab-id under the shell's shared widget-layout atom (see CONSOLIDATED_DEFAULT_LAYOUTS).
const GOOGLE_OVERVIEW_LAYOUT_KEY = 'google-overview';

// perf-v2 OverviewTab navigates by its own tab ids; map them onto the
// consolidated surface's tab ids (ads → creative-ads, keywords/search-terms →
// keywords-terms; the rest are 1:1).
const OVERVIEW_TAB_NAV_MAP: Record<string, string> = {
  overview: 'overview',
  campaigns: 'campaigns',
  ads: 'creative-ads',
  keywords: 'keywords-terms',
  'search-terms': 'keywords-terms',
  audience: 'audience',
  budget: 'budget',
  activity: 'activity',
};

/**
 * Consolidated Overview tab (#5971). A static cross-platform top block (unified
 * KPI summary + performance chart, shown for every scope) followed by the full
 * perf-v2 Google Overview — all its widgets — whenever Google is in scope. The
 * Google detail is fed by the shared usePerformanceData hook (the same data
 * layer /performance-v2 uses), so there is no duplicated computation.
 *
 * Renders OverviewTab's own WidgetGrid; the top block is plain (non-grid), so
 * only one react-dnd provider is ever mounted on this tab.
 */
export function ConsolidatedOverviewPanel({
  scope,
  getTabLayout,
  onReorder,
  onResize,
}: {
  scope: PlatformScope;
  getTabLayout: (tabId: string) => WidgetConfig[];
  onReorder: (tabId: string, layout: WidgetConfig[]) => void;
  onResize: (tabId: string, id: string, width: 33 | 50 | 66 | 100) => void;
}) {
  const googleInScope = platformsInScope(scope).includes('google');
  return (
    <div className="space-y-6">
      <OverviewKpis scope={scope} />
      <OverviewChart scope={scope} />
      {googleInScope && (
        <GoogleOverviewDetail getTabLayout={getTabLayout} onReorder={onReorder} onResize={onResize} />
      )}
    </div>
  );
}

/** The Google-specific Overview widgets, reusing perf-v2's OverviewTab verbatim. */
function GoogleOverviewDetail({
  getTabLayout,
  onReorder,
  onResize,
}: {
  getTabLayout: (tabId: string) => WidgetConfig[];
  onReorder: (tabId: string, layout: WidgetConfig[]) => void;
  onResize: (tabId: string, id: string, width: 33 | 50 | 66 | 100) => void;
}) {
  const { symbol } = useCurrencySymbol('google');
  const cs = symbol ?? '';
  const navigate = useNavigate();

  // Driven by the shared filter toolbar (#5971); tab='overview' gates the
  // Overview-only queries (insights, recommendations, conversion actions, …).
  const selectedDays = useAtomValue(consolidatedPerformanceDateRangeAtom);
  const groupBy = useAtomValue(consolidatedPerformanceGroupByAtom);
  const comparison = useAtomValue(consolidatedPerformanceComparisonAtom);
  const campaignTypes = useAtomValue(consolidatedPerformanceCampaignTypesAtom);
  const selectedCampaignIds = useAtomValue(consolidatedPerformanceCampaignIdsAtom);
  const includeExternal = useAtomValue(consolidatedPerformanceIncludeExternalAtom);

  const typeFilter = useMemo<(type: string) => boolean>(() => {
    if (campaignTypes.size === 0 || campaignTypes.has('all')) return () => true;
    return (type: string) => campaignTypes.has(type);
  }, [campaignTypes]);

  const perf = usePerformanceData({
    includeExternal,
    tab: 'overview',
    campaignSubView: 'by-type',
    comparisonMode: comparison,
    selectedCampaignIds,
    chartDateRange: selectedDays,
    chartGroupBy: groupBy,
    showUserEdits: false,
    selectedMetric: 'conversions',
    selectedDays,
    typeFilter,
    cs,
  });

  return (
    <OverviewTab
      hasData={perf.hasData}
      accountSnapshot={perf.accountSnapshot}
      problemAlerts={perf.problemAlerts}
      spendTrends={perf.spendTrends}
      insightsData={perf.insightsData}
      isInsightsLoading={perf.isInsightsLoading}
      isSearchTermsLoading={perf.isSearchTermsLoading}
      isConversionActionsLoading={perf.isConversionActionsLoading}
      notableSearchTermsByCampaign={perf.notableSearchTermsByCampaign}
      conversionMixByAction={perf.conversionMixByAction}
      totals={perf.totals}
      previousTotals={perf.previousTotals}
      topRecommendationsData={perf.topRecommendationsData}
      isTopRecommendationsLoading={perf.isTopRecommendationsLoading}
      typeMetrics={perf.typeMetrics}
      costByType={perf.costByType}
      convByType={perf.convByType}
      conversionActionsData={perf.conversionActionsData}
      searchTermsData={perf.searchTermsData}
      adMetrics={perf.adMetrics}
      actionLogs={perf.actionLogs}
      budgetUtilisation={perf.budgetUtilisation}
      filteredCampaigns={perf.filteredCampaigns}
      currencySymbol={cs}
      onNavigateTab={(t) => navigate({ to: '/performance-consolidated', hash: OVERVIEW_TAB_NAV_MAP[t] ?? 'overview' })}
      // Time-series lives in the top block's cross-platform chart, so the
      // Google grid omits 'performance-chart' from its layout and this is never called.
      renderChart={() => null}
      layout={getTabLayout(GOOGLE_OVERVIEW_LAYOUT_KEY)}
      onReorder={(l) => onReorder(GOOGLE_OVERVIEW_LAYOUT_KEY, l)}
      onResize={(id, w) => onResize(GOOGLE_OVERVIEW_LAYOUT_KEY, id, w)}
    />
  );
}
